import React from 'react';
import PropTypes from 'prop-types';

import {
  Tabs,
  Tab,
  Button,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import NewReleasesOutlinedIcon from '@material-ui/icons/NewReleasesOutlined';

import { attributeOptions, formatNumber, hazardDefinitions, cityoverrides } from '../../Utilities/Helpers';
import { regionalOverviews } from '../../Utilities/OverviewText.js';

import './Template.scss';

const tabHeight = '50px';
const tabWidth = '50%';
const useStyles = makeStyles(() => ({
  tabsRoot: {
    minHeight: tabHeight,
    height: tabHeight,
    backgroundColor: 'rgba(0,0,0,0.06)',
    width: '100%',
  },
  tabRegionalRoot: {
    minHeight: tabHeight,
    height: tabHeight,
    maxWidth: tabWidth,
    minWidth: tabWidth,
    borderBottom: '2px solid rgba(0,0,0,0.2)',
    borderRight: '1px solid rgba(0,0,0,0.1)',
    backgroundColor: 'rgba(0,0,0,0.06)',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.12)'
    }
  },
  tabLocalRoot: {
    minHeight: tabHeight,
    height: tabHeight,
    maxWidth: tabWidth,
    minWidth: tabWidth,
    borderBottom: '2px solid rgba(0,0,0,0.2)',
    borderRight: '1px solid rgba(0,0,0,0.1)',
    backgroundColor: 'rgba(0,0,0,0.06)',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.12)'
    }
  },
}));



export default function Template({
  chartTab,
  chartVisible,
  climateOption,
  climateRange,
  countyClimateTrends,
  countyHazardMapData,
  hazardOptions,
  hazardRange,
  loc,
  mode,
  modelTrendData,
  showBadge,
  stateClimateTrends,
  stateHazardMapData,
  tab,
  theme,
  handleOptionsChange
}) {
  const classes = useStyles();


  const handleShowClimateData = (past) => {
    handleOptionsChange(past ? 0 : 1, 'chartTab');
    handleButtonClick();
  };
  

  const handleButtonClick = () => {
    handleOptionsChange(!chartVisible, 'chartVisible');
  };


  const handleTabChange = (event, newValue) => {
    if (newValue === 0) {
      handleOptionsChange([[newValue, 'tab'], [false, 'showBadge']], 'multiple');
    } else {
      handleOptionsChange(newValue, 'tab');
    }
  };


  // Not currently used, but may be asked for later
  // const handleInfoButton = () => {
  //   window.open("https://google.com/");
  // };


  const renderText = () => {
    if(!tab) {
      const CISA = ['NC', 'SC'];
      const GLISA = ['MN', 'WI', 'MI', 'IL', 'IN', 'OH'];
      const MARISA = ['VA', 'WV', 'MD', 'PA', 'DE', 'NJ', 'NY', 'DC'];
      let stateAbbr = loc.state ? loc.state : loc.id;
      let region = 'Default';
      
      if (CISA.includes(stateAbbr)) {
        region = 'CISA';
      } else if (GLISA.includes(stateAbbr)) {
        region = 'GLISA';
      } else if (MARISA.includes(stateAbbr)) {
        region = 'MARISA';
      }
      
      let overview = regionalOverviews[theme][region].length === 0 ? regionalOverviews[theme]['Default'] : regionalOverviews[theme][region];
      
      return (
        <div id="template-text">
          <Typography component="h2" variant="h4">Overview</Typography>
          <Typography>{overview}</Typography>
        </div>
      );
    } else if(!loc) {
      return <Typography>Please click on a state/county to view detailed data.</Typography>;
    } else {
      let place = loc.name + (loc.state ? (cityoverrides.includes(loc.id) ? ' City, ' : ' County, ') : '') + (loc.state ? loc.state : '');
      let text = `In ${place}, data on past ${mode ? attributeOptions[climateOption]['text'] : hazardOptions[0].toLowerCase()}`;
      let notes = [];
      
      if(!mode){
        try {
          let data = loc.id.length > 2 ? countyHazardMapData : stateHazardMapData;
          let count = (data['count'] && data['count'][loc.id]) || 0;
          let eventPlurality = count === 1 ? 'event' : 'events';
          let fullPlurality = count === 1 ? 'This event' : 'These events';
          let damageCrops = (data['damageCrops'] && data['damageCrops'][loc.id]) || 0;
          let damageProperty = (data['damageProperty'] && data['damageProperty'][loc.id]) || 0;
          let deaths = (data['deaths'] && data['deaths'][loc.id]) || 0;
          let injuries = (data['injuries'] && data['injuries'][loc.id]) || 0;

          text += ` episodes show that ${place} reported experiencing ${count} ${hazardOptions[0].toLowerCase()} ${eventPlurality} between ${hazardRange.start} and ${hazardRange.end}. ${fullPlurality} resulted in `;
          
          let buts = [];
          let ins = [];
          let wereWas = 'were';

          if (damageProperty > 0) {
            ins.push(`$${formatNumber(damageProperty)} in reported property damage`);
          } else {
            buts.push('no reported property damage');
            wereWas = 'was';
          }
          
          if (damageCrops > 0) {
            ins.push(`$${formatNumber(damageCrops)} in reported crop damage`);
          } else {
            buts.push('no reported crop damage');
            wereWas = 'was';
          }
          
          if (deaths > 0) {
            ins.push(`${deaths} reported deaths`);
          } else {
            buts.push('no reported deaths');
          }

          if (injuries > 0) {
            ins.push(`${injuries} reported injuries`);
          } else {
            buts.push('no reported injuries');
          }

          if (buts.length === 4) {
            text += 'no reported property damage, no reported crop damage, no reported injuries, and no reported deaths.';
          } else {
            if (ins.length === 2) {
              text += `${ins[0]} and ${ins[1]}.`;
            } else if (ins.length > 2) {
              text += `${ins.slice(0, ins.length - 1).join(', ')}, and ${ins.slice(ins.length - 1)}.`;
            } else {
              text += `${ins[0]}.`;
            }

            if (buts.length === 2) {
              text += ` There ${wereWas} ${buts[0]} and ${buts[1]}.`;
            } else if (buts.length > 2) {
              text += ` There ${wereWas} ${buts.slice(0, buts.length - 1).join(', ')}, and ${buts.slice(buts.length - 1)}.`;
            } else if (buts.length === 1) {
              text += ` There ${wereWas} ${buts[0]}.`;
            }
          }

          notes.push(
            <div key="hazdef">
              <Typography >{hazardDefinitions[hazardOptions[0]]}</Typography>
              <hr style={{ width: '90%', margin: '4px auto 8px auto' }}></hr>
              <Typography style={{ fontSize: '12px', fontStyle: 'italic' }}><a style={{ textDecoration: 'none' }} target="_blank" href="https://www.nws.noaa.gov/directives/sym/pd01016005curr.pdf" rel="noreferrer">National Weather Service Instruction 10-1605, Storm Data Preparation</a></Typography>
            </div>
          );
          
          notes.push(<Typography key="noaancei"><b>SOURCE:</b> NOAA NCEI <a target="_blank" href="https://www.ncdc.noaa.gov/stormevents/" rel="noreferrer">Storm Events Database</a>. Please note that the National Weather Service does not guarantee the accuracy, validity, or completeness of the event data in the Storm Events Database. For additional information about the limitations of this data and guidance for its appropriate use, please refer to the Data and Technical Information tab and Chapter 2 of the Climate Hazard and Mitigation Planning (CHaMP) Tool: Background and Guidance for Users Report.</Typography>);
        } catch {
          text = '';
        }
      } else {
        if (chartTab !== 0 && chartVisible && mode) {
          try {
            let pastAverage = modelTrendData['past']['mean'].reduce((acc, currVal) => acc + currVal) / 30;
            let futureAverage = modelTrendData['future']['mean'].reduce((acc, currVal) => acc + currVal) / 30;
            let diff = futureAverage - pastAverage;
            let eFuture = chartTab === 1 ? '8.5' : '4.5';
            let highLow = chartTab === 1 ? 'high' : 'low';
            
            text = [`In ${place}, climate model projections of the ${attributeOptions[climateOption]['text']} for a ${highLow} emissions future (`, <a key="loca" target="_blank" rel="noreferrer" href="http://loca.ucsd.edu/">LOCA RCP {eFuture}</a>, `) shows an average ${diff > 0 ? 'increase' : 'decrease'} by ${Math.abs(diff).toFixed(2)}${attributeOptions[climateOption]['units']} when comparing ${modelTrendData['future']['year'][0]}-${modelTrendData['future']['year'][29]} to ${modelTrendData['past']['year'][0]}-${modelTrendData['past']['year'][29]}.`];

            notes.push(<Typography key="locarcp"><b>SOURCE:</b> <a target="_blank" href={eFuture === '8.5' ? 'https://link.springer.com/article/10.1007%2Fs10584-011-0149-y' : 'https://link.springer.com/article/10.1007%2Fs10584-011-0151-4'} rel="noreferrer">LOCA RCP {eFuture}</a> accessed through <a target="_blank" href="https://www.rcc-acis.org/" rel="noreferrer">ACIS</a>.</Typography>);
            notes.push(<Typography key="disc1"><b>NOTE:</b> Climate model data is best used to look at the overall amount and rate of change over time rather than as a forecast for a single year. The climate model scenarios (i.e. RCPs) are representations of future concentrations of greenhouse gases, aerosols, other gases, and changes in land use/cover. The data represented in these charts show the range of possible climate changes from 32 different climate models that were each run under the same set of assumptions. The average of these 32 models is shown in black. No single model is a best estimate of the future, therefore it is important to consider the full range of changes presented in the chart, rather than a single projection, to understand what your region/state/county could expect to see due to a changing climate. Additionally, actual future conditions may be outside of the range of the model data.</Typography>);
            
            if (climateOption.includes('pcpn')) {
              notes.push(<Typography key="disc2"><b>NOTE:</b> When applying future projected precipitation data to planning efforts, particularly when examining future extremes, best practice suggests looking at the difference between a baseline period (such as 1991-2020) and a future period of interest for planning (such as 2021-2050), rather than using the magnitude of precipitation directly from future projections. This is an important step to consider, because climate models are not able to resolve many of the physical processes that drive the most extreme rainfall events. Given this limited ability to simulate individual extreme events and the possibility that future rainfall extremes may be larger than those projected by the models are generally better at representing the general change in magnitude of such events.</Typography>);
            }
          } catch {
            text = '';
          }
        } else {
          let units = attributeOptions[climateOption]['units'];
          let trend;
          try {
            trend = loc.state === undefined ? stateClimateTrends[climateOption][loc.id] : countyClimateTrends[climateOption][loc.id];
          } catch {
            trend = 0;
          }

          text += `show a trend of ${trend > 0 ? 'increasing' : 'decreasing'} by ${Math.abs(trend).toFixed(2)}${units} per decade between ${climateRange.start} and ${climateRange.end}.`;

          notes.push(<Typography key="acis"><b>SOURCE:</b> Applied Climate Information System (<a target="_blank" href="https://www.rcc-acis.org/" rel="noreferrer">ACIS</a>) operated and maintained by the <a href="https://www.ncdc.noaa.gov/customer-support/partnerships/regional-climate-centers">NOAA Regional Climate Center Program</a>. For additional information about this data and guidance for its appropriate use, please refer to the Data and Technical Information tab and Chapter 2 of the Climate Hazard and Mitigation Planning (CHaMP) Tool: Background and Guidance for Users Report.</Typography>);
        }
      }

      return (
        <div id="template-text">
          <Typography component="h2" variant="h4">{place}</Typography>
          
          <Typography>
            {text}
          </Typography>
          
          {notes}
        </div>
      );
    }
  };


  return (
    <div id="template" className="card">
      <div className="tabs-cont">
        <Tabs className={classes.tabsRoot} value = {tab} onChange = {handleTabChange} variant="fullWidth">
          <Tab className={classes.tabRegionalRoot} label={<div id="badge-tab"><NewReleasesOutlinedIcon id="new-overview" className={showBadge ? 'faded-in' : 'faded-out'}/>Regional Overview</div>} />
          <Tab className={classes.tabLocalRoot} label={`${(loc && loc.state) ? 'County' : 'State'} Details`}/>
        </Tabs>
      </div>
      
      {renderText()}
      
      {/* Not currently used, but may be asked for later
      {
        loc && tab === 1 && loc.state && <Button variant="contained" color="secondary" onClick={handleInfoButton}>
          More County Information
        </Button>
      } 
      */}
      
      {loc && tab === 1 && !loc.state && <Typography variant="caption" style={{textAlign: 'center'}}>Click on a county for more location specific information</Typography>}
      
      {loc && !mode && <Button variant="contained" color="primary" onClick={handleButtonClick}>
        {`${chartVisible ? 'Hide' : 'Show'} Annual Data`}
      </Button>}

      {loc && mode && (chartVisible ? 
        <Button variant="contained" color="primary" onClick={handleButtonClick}>Hide Chart</Button>
        :
        <div className="two-btns">
          <Button variant="contained" color="primary" onClick={() => handleShowClimateData(true)}>Show Historical Data</Button>
          <Button variant="contained" color="primary" onClick={() => handleShowClimateData(false)}>Show Projected Data</Button>
        </div>)
      }
    </div>
  );
}

Template.propTypes = {
  chartTab: PropTypes.number,
  chartVisible: PropTypes.bool,
  climateOption: PropTypes.string,
  climateRange: PropTypes.object,
  countyClimateTrends: PropTypes.object,
  countyHazardMapData: PropTypes.object,
  hazardOptions: PropTypes.array,
  hazardRange: PropTypes.object,
  loc: PropTypes.object,
  mode: PropTypes.bool,
  modelTrendData: PropTypes.object,
  showBadge: PropTypes.bool,
  stateClimateTrends: PropTypes.object,
  stateHazardMapData: PropTypes.object,
  tab: PropTypes.number,
  theme: PropTypes.string,
  handleOptionsChange: PropTypes.func
};